import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from "react";

export interface Track {
  id: string;
  title: string;
  filename: string;
  path: string;
}

interface MusicPlayerContextType {
  // State
  isPlaying: boolean;
  currentTrackIndex: number;
  volume: number;
  isShuffled: boolean;
  repeatMode: "off" | "all" | "one";
  playlist: Track[];
  currentTime: number;
  duration: number;
  isExpanded: boolean;
  
  // Actions
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  next: () => void;
  previous: () => void;
  setVolume: (volume: number) => void;
  toggleShuffle: () => void;
  setRepeatMode: (mode: "off" | "all" | "one") => void;
  setTrack: (index: number) => void;
  setCurrentTime: (time: number) => void;
  setPlaylist: (tracks: Track[]) => void;
  toggleExpanded: () => void;
  setExpanded: (expanded: boolean) => void;
  
  // Audio element ref (for direct access if needed)
  audioRef: React.RefObject<HTMLAudioElement | null>;
}

const MusicPlayerContext = createContext<MusicPlayerContextType | undefined>(undefined);

export function MusicPlayerProvider({ children }: { children: ReactNode }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [volume, setVolumeState] = useState<number>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("musicPlayerVolume");
      return saved ? parseFloat(saved) : 0.7;
    }
    return 0.7;
  });
  const [isShuffled, setIsShuffled] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("musicPlayerShuffle");
      return saved === "true";
    }
    return false;
  });
  const [repeatMode, setRepeatModeState] = useState<"off" | "all" | "one">(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("musicPlayerRepeat") as "off" | "all" | "one" | null;
      return saved || "off";
    }
    return "off";
  });
  const [playlist, setPlaylist] = useState<Track[]>([]);
  const [currentTime, setCurrentTimeState] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const shuffledIndicesRef = useRef<number[]>([]);

  // Persist volume to localStorage
  useEffect(() => {
    localStorage.setItem("musicPlayerVolume", String(volume));
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Persist shuffle to localStorage
  useEffect(() => {
    localStorage.setItem("musicPlayerShuffle", String(isShuffled));
  }, [isShuffled]);

  // Persist repeat mode to localStorage
  useEffect(() => {
    localStorage.setItem("musicPlayerRepeat", repeatMode);
  }, [repeatMode]);

  // Generate shuffled indices when shuffle is enabled or playlist changes
  useEffect(() => {
    if (isShuffled && playlist.length > 0) {
      const indices: number[] = Array.from({ length: playlist.length }, (_, i) => i);
      // Fisher-Yates shuffle
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const temp = indices[i]!;
        const swap = indices[j];
        if (swap !== undefined) {
          indices[i] = swap;
          indices[j] = temp;
        }
      }
      shuffledIndicesRef.current = indices;
    }
  }, [isShuffled, playlist]);

  // Get the actual track index (accounting for shuffle)
  const getActualTrackIndex = useCallback((index: number): number => {
    if (isShuffled && shuffledIndicesRef.current.length > 0) {
      const idx = shuffledIndicesRef.current[index];
      return idx !== undefined ? idx : 0;
    }
    return index;
  }, [isShuffled]);

  const next = useCallback(() => {
    if (playlist.length === 0) return;
    
    if (isShuffled) {
      const currentShuffledIndex = shuffledIndicesRef.current.indexOf(getActualTrackIndex(currentTrackIndex));
      const nextShuffledIndex = (currentShuffledIndex + 1) % shuffledIndicesRef.current.length;
      setCurrentTrackIndex(nextShuffledIndex);
    } else {
      const nextIndex = (currentTrackIndex + 1) % playlist.length;
      setCurrentTrackIndex(nextIndex);
    }
  }, [playlist.length, isShuffled, currentTrackIndex, getActualTrackIndex]);

  // Handle track end
  const handleTrackEnd = useCallback(() => {
    if (repeatMode === "one") {
      // Repeat current track
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {
          setIsPlaying(false);
        });
      }
    } else if (repeatMode === "all" || currentTrackIndex < playlist.length - 1) {
      // Move to next track - ensure isPlaying stays true so the new track auto-plays
      setIsPlaying(true);
      next();
    } else {
      // End of playlist
      setIsPlaying(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  }, [repeatMode, currentTrackIndex, playlist.length, next]);

  // Update audio source when track changes or playlist is first loaded
  // Note: We don't include isPlaying in deps to avoid reloading when play/pause toggles
  useEffect(() => {
    if (audioRef.current && playlist.length > 0 && currentTrackIndex < playlist.length) {
      const actualIndex = getActualTrackIndex(currentTrackIndex);
      const track = playlist[actualIndex];
      if (track) {
        // Only reload if the source actually changed
        const currentSrc = audioRef.current.src;
        const needsNewSource = !currentSrc || !currentSrc.includes(track.path);
        
        if (needsNewSource) {
          // Set the source and wait for it to load
          audioRef.current.src = track.path;
          audioRef.current.load();
          
          // If we were playing, resume after the new track loads
          if (isPlaying) {
            const playAfterLoad = () => {
              audioRef.current?.play().catch((error) => {
                console.error("Failed to play audio:", error);
                setIsPlaying(false);
              });
            };
            if (audioRef.current.readyState >= 2) {
              // HAVE_CURRENT_DATA - can play
              playAfterLoad();
            } else {
              audioRef.current.addEventListener("canplay", playAfterLoad, { once: true });
            }
          }
        }
      }
    }
  }, [currentTrackIndex, playlist, getActualTrackIndex]); // Removed isPlaying from deps

  // Set up audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTimeState(audio.currentTime);
    const updateDuration = () => {
      const dur = audio.duration;
      if (dur !== undefined && isFinite(dur)) {
        setDuration(dur);
      }
    };
    const handleEnded = () => handleTrackEnd();
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => {
      // When a track naturally ends, some browsers also emit a pause event.
      // In that case we don't want to flip isPlaying to false, otherwise
      // auto-advance won't be able to start the next track.
      if (audio.ended) {
        return;
      }
      setIsPlaying(false);
    };

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
    };
  }, [handleTrackEnd]);

  const play = useCallback(() => {
    if (!audioRef.current || playlist.length === 0) {
      return;
    }
    
    // Ensure audio source is set before playing
    const actualIndex = getActualTrackIndex(currentTrackIndex);
    const track = playlist[actualIndex];
    if (!track) {
      return; // No track available
    }
    
    // Check if we're resuming playback or starting a new track
    const currentSrc = audioRef.current.src;
    const needsNewSource = !currentSrc || !currentSrc.includes(track.path);
    
    const attemptPlay = () => {
      if (!audioRef.current) return;
      
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Playback started successfully
            setIsPlaying(true);
          })
          .catch((error) => {
            // Handle play() rejection
            console.error("Failed to play audio:", error);
            console.error("Error details:", {
              name: error.name,
              message: error.message,
              track: track.path,
              readyState: audioRef.current?.readyState,
            });
            setIsPlaying(false);
          });
      }
    };
    
    // Only reload if we need to change the source
    if (needsNewSource) {
      // Set the source and wait for it to load
      audioRef.current.src = track.path;
      audioRef.current.load();
      
      // Wait for the audio to be ready before playing
      const handleCanPlay = () => {
        attemptPlay();
      };
      
      // Also handle errors during loading
      const handleError = (e: Event) => {
        console.error("Audio loading error:", e);
        const audio = e.target as HTMLAudioElement;
        console.error("Audio error details:", {
          error: audio.error,
          code: audio.error?.code,
          message: audio.error?.message,
          track: track.path,
        });
        setIsPlaying(false);
      };
      
      audioRef.current.addEventListener("error", handleError, { once: true });
      
      if (audioRef.current.readyState >= 2) {
        // HAVE_CURRENT_DATA - can play immediately
        attemptPlay();
      } else {
        // Wait for canplay event
        audioRef.current.addEventListener("canplay", handleCanPlay, { once: true });
      }
    } else {
      // Source is already set - just resume playback from current position
      // This preserves the currentTime and doesn't reset to the beginning
      attemptPlay();
    }
  }, [playlist, currentTrackIndex, getActualTrackIndex]);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  const previous = useCallback(() => {
    if (playlist.length === 0) return;
    
    if (isShuffled) {
      const currentShuffledIndex = shuffledIndicesRef.current.indexOf(getActualTrackIndex(currentTrackIndex));
      const prevShuffledIndex = currentShuffledIndex === 0 
        ? shuffledIndicesRef.current.length - 1 
        : currentShuffledIndex - 1;
      setCurrentTrackIndex(prevShuffledIndex);
    } else {
      const prevIndex = currentTrackIndex === 0 ? playlist.length - 1 : currentTrackIndex - 1;
      setCurrentTrackIndex(prevIndex);
    }
  }, [playlist.length, isShuffled, currentTrackIndex, getActualTrackIndex]);

  const setVolume = useCallback((newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);
  }, []);

  const toggleShuffle = useCallback(() => {
    setIsShuffled((prev) => !prev);
  }, []);

  const setRepeatMode = useCallback((mode: "off" | "all" | "one") => {
    setRepeatModeState(mode);
  }, []);

  const setTrack = useCallback((index: number) => {
    if (index >= 0 && index < playlist.length) {
      setCurrentTrackIndex(index);
    }
  }, [playlist.length]);

  const setCurrentTime = useCallback((time: number) => {
    if (audioRef.current && duration > 0) {
      audioRef.current.currentTime = Math.max(0, Math.min(time, duration));
    }
  }, [duration]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const setExpanded = useCallback((expanded: boolean) => {
    setIsExpanded(expanded);
  }, []);

  // Get current track
  const currentTrack = playlist.length > 0 && currentTrackIndex < playlist.length
    ? playlist[getActualTrackIndex(currentTrackIndex)]
    : null;

  return (
    <MusicPlayerContext.Provider
      value={{
        isPlaying,
        currentTrackIndex,
        volume,
        isShuffled,
        repeatMode,
        playlist,
        currentTime,
        duration,
        isExpanded,
        play,
        pause,
        togglePlay,
        next,
        previous,
        setVolume,
        toggleShuffle,
        setRepeatMode,
        setTrack,
        setCurrentTime,
        setPlaylist,
        toggleExpanded,
        setExpanded,
        audioRef,
      }}
    >
      <audio ref={audioRef} />
      {children}
    </MusicPlayerContext.Provider>
  );
}

export function useMusicPlayer() {
  const context = useContext(MusicPlayerContext);
  if (context === undefined) {
    throw new Error("useMusicPlayer must be used within a MusicPlayerProvider");
  }
  return context;
}

